import React, { ReactNode } from "react";

import {Grid, Container} from "@mui/material";
import styled from "styled-components";
import Carousel from "../components/blocks/Carousel";
import EnquiryForm from "../components/blockComponents/EnquiryForm";
import { media } from "../../styles/styles";
import { Page, Card, TeamMember, AllPages } from "../types";
import { appSelector } from "../redux/app/selectors";
import ChequeredStrip from "../components/blockComponents/ChequeredStrip";
import Link from "next/link";
import ArrowLink from "../components/blockComponents/ArrowLink";
import PageContainerBlocks from "./PageContainerBlocks";

interface Props {
  page: Page;
  posts: Card[];
  teamMembers?: TeamMember[];
  bodyComponents?: ReactNode[];
  allPages: AllPages;
}

interface TintProps {
  bgColor?: string;
  bgImage?: boolean;
}
interface ContainerWrapperProps {
  bgColor?: string;
  bgImage?: string;
  hasMargin?: boolean;
  negativeMargin?: boolean;
  marginOptions?: string[];
  centered?: boolean;
}
interface RowProps {
  breakpoint?: "desktop" | "tablet" | "phone";
  wrap?: boolean;
  sectionType?: string;
}
interface TitleProps {
  template?: string;
}
const ArrowLinkWrapper = styled.div`
  margin: 1rem 0;
  font-size: 1.3rem;
`;
const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right;
`;
const SubTitle = styled.h3`
  font-weight: 300;
  font-size: 1.2rem;
`;
const Tint = styled('div').withConfig({
  shouldForwardProp: (prop) => !['bgColor', 'bgImage'].includes(prop)
})<TintProps>`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  ${({ bgColor, bgImage }) => {
    if (bgColor === "Dark Grey")
      return `background-color:rgba(50, 64, 55, ${bgImage ? `.9` : `1`});`;
    else if (bgColor === "Grey") return `background-color:#F0F0F0;`;
  }}
`;
const ContainerWrapper = styled('div').withConfig({
  shouldForwardProp: (prop) => !['bgColor', 'bgImage', 'hasMargin', 'negativeMargin', 'marginOptions', 'centered'].includes(prop)
})<ContainerWrapperProps>`
  ${({ negativeMargin, theme }) =>
    negativeMargin
      ? `&#section-0 {
    margin-top: -3rem;
    z-index: 2;
    position: relative;
  }`
      : `&#section-0 {border-bottom:solid 1px #E6E6E6; }`}
  box-shadow: ${({ bgColor }) =>
    bgColor === "Maroon" ? `inset 0 0 0 1000px rgba(118, 50, 63,.70)` : `none`};
  color: ${({ bgColor }) =>
    bgColor && bgColor !== "White" && bgColor !== "Grey" ? `#fff` : `#000`};

  ${media.desktop`
  &#section-2 {
    margin-top:0;
  }
  `}
  background-size: cover;
  margin: 2rem;
  ${media.tablet`
  margin: .5rem 0;
  `};
  ${({ hasMargin, marginOptions }) => {
    let options = "";
    if (marginOptions) {
      if (marginOptions.includes("No top margin"))
        options += `
      margin-top: 0 !important;`;
      if (marginOptions.includes("No bottom margin"))
        options += `
        margin-bottom: 0 !important;`;
    }
    if (!hasMargin)
      options += `
    margin-left: 0;
    margin-right: 0;`;
    return options;
  }}

  position: relative;
`;
const StyledContainer = styled(Container).withConfig({
  shouldForwardProp: (prop) => !['bgColor', 'bgImage', 'hasMargin', 'negativeMargin', 'marginOptions', 'centered'].includes(prop)
})<ContainerWrapperProps>`
  position: relative;
  overflow: hidden;
  ${({ bgColor }) =>
    bgColor === "White" &&
    `
    border-radius: 6px;
    padding-top: 2rem;
    background-color:#fff;
    `}
  ${({ centered }) => centered && `text-align: center;`}
`;
const Row = styled(Grid).withConfig({
  shouldForwardProp: (prop) => !['sectionType'].includes(prop)
})<RowProps>`
  && {
    z-index: 2;
    position: relative;
    ${({ sectionType }) =>
      sectionType === `2:1` &&
      `>div:nth-child(odd){flex: 2;max-width:66.6%;

    } >div:nth-child(even){flex: 1;}`};

    ${({ sectionType }) => {
      if (sectionType === `2:1`)
        return media.tablet`display:block;
     >div:nth-child(odd){max-width:100%;
    }`;
    }}
  }
`;

const Title = styled('h2').withConfig({
  shouldForwardProp: (prop) => !['template'].includes(prop)
})<TitleProps>`
  ${({ template }) => template === "Home" && `text-align: center;`}
  margin-top: 0;
  ${media.phone`
  margin: 0;
  `}
  font-size: 1.8rem;
  position: relative;
`;

const getSection = (
  sectionType,
  section,
  sectionIndex,
  app,
  posts,
  template,
  teamMembers,
  category,
  operatingTimes,
) => {
  const content = PageContainerBlocks(
    section.blocks,
    sectionIndex,
    app,
    posts,
    template,
    teamMembers,
    category
  );
  let sectionInner;
  switch (section.sectionType) {
    case "Carousel":
      sectionInner = (
        <Carousel
          id={`${sectionType}-${sectionIndex}`}
          key={`${sectionType}-${sectionIndex}`}
          blocks={content}
          sectionTitle={<Title>{section.title}</Title>}
        />
      );
      break;
    case "Contact":
      sectionInner = (
          <StyledContainer
            bgColor={section.backgroundColour}
            key={`${sectionType}-${sectionIndex}`}
            id={`${sectionType}-${sectionIndex}`}
          >
            <EnquiryForm operatingTimes={operatingTimes} />
          </StyledContainer>
        )
      break;
    case "Full Width":
      sectionInner = (
        <div
          key={`${sectionType}-${sectionIndex}`}
          id={`${sectionType}-${sectionIndex}`}
        >
          {section.title && <Title template={template}>{section.title}</Title>}
          <Row container>{content}</Row>
          {section.backgroundColour === "Grey" && (
            <ChequeredStrip location="bottom" />
          )}
        </div>
      );
      break;
    case "Half Width": // TODO: fix (Stewart note: No idea what's broken here)
      sectionInner = (
        <StyledContainer
          bgColor={section.backgroundColour}
          key={`${sectionType}-${sectionIndex}`}
          id={`${sectionType}-${sectionIndex}`}
        >
          {section.title && <Title template={template}>{section.title}</Title>}
          <Row container sectionType={section.sectionType}>
            {content}
          </Row>
        </StyledContainer>
      );
      break;
    case "Offer Cards": // TODO: fix (Stewart note: No idea what's broken here)
      sectionInner = (
        <StyledContainer
          bgColor={section.backgroundColour}
          key={`${sectionType}-${sectionIndex}`}
          id={`${sectionType}-${sectionIndex}`}
          centered
        >
          {section.title && <Title template={template}>{section.title}</Title>}
          <SubTitle>
            End to end legal support for emergency services workers including
            those in and out of uniform.
          </SubTitle>
          <Row
            container
            sectionType={section.sectionType}
            justifyContent={section.sectionType === "Center" ? "center" : null}
          >
            {content}
          </Row>
          <ArrowLinkWrapper>
            <Link href="/our-offer-to-you">
              <span>
                <ArrowLink arrow="post">Find out more</ArrowLink>
              </span>
            </Link>
          </ArrowLinkWrapper>
        </StyledContainer>
      );
      break;
    default:
      sectionInner = (
        <StyledContainer
          bgColor={section.backgroundColour}
          key={`${sectionType}-${sectionIndex}`}
          id={`${sectionType}-${sectionIndex}`}
        >
          {section.title && <Title template={template}>{section.title}</Title>}
          <Row
            container
            sectionType={section.sectionType}
            justifyContent={section.sectionType === "Center" ? "center" : null}
          >
            {content}
          </Row>
        </StyledContainer>
      );
      break;
  }
  return (
    <ContainerWrapper
      bgColor={section.backgroundColour}
      id={`${sectionType}-${sectionIndex}`}
      hasMargin={
        section.sectionType !== "Full Width" &&
        section.sectionType !== "Carousel"
      }
      negativeMargin={template === "Home"}
      marginOptions={section.marginOptions}
      key={`${sectionType}-${sectionIndex}`}
    >
      {section.backgroundImage && (
        <picture>
          <source
            type="image/webp"
            srcSet={`${section.backgroundImage.fields.file.url}?fm=webp&w=360&q=100`}
            media="(max-width: 400px)"
          />
          <source
            type="image/webp"
            srcSet={`${section.backgroundImage.fields.file.url}?fm=webp&q=100`}
            media="(min-width: 401px)"
          />
          <source
            type="image/jpeg"
            srcSet={`${section.backgroundImage.fields.file.url}?w=360&q=100`}
            media="(max-width: 400px)"
          />
          <source
            type="image/jpeg"
            srcSet={`${section.backgroundImage.fields.file.url}?q=100`}
            media="(min-width: 401px)"
          />
          <BackgroundImage
            src={`${section.backgroundImage.fields.file.url}?q=100`}
            alt={section.backgroundImage.fields.title}
          />
        </picture>
      )}
      <Tint
        bgColor={section.backgroundColour}
        bgImage={!!section.backgroundImage}
      />
      {sectionInner}
    </ContainerWrapper>
  );
};

const PageContainer: React.FunctionComponent<Props> = ({
  page,
  posts,
  teamMembers,
  bodyComponents,
  allPages: { operatingTimes }
}) => {
  const app = appSelector((state) => state.app);
  // Component State
  const heroSections = page.hero.map(({ fields: section }, sectionIndex) =>
    getSection(
      `hero`,
      section,
      sectionIndex,
      app,
      posts,
      page.template,
      teamMembers,
      page.category,
      operatingTimes
    )
  );
  const bodySections = page.sections.map(({ fields: section }, sectionIndex) =>
    getSection(
      `section`,
      section,
      sectionIndex,
      app,
      posts,
      page.template,
      teamMembers,
      page.category,
      operatingTimes
    )
  );

  return (
    <>
      {heroSections}
      {bodyComponents}
      {bodySections}
    </>
  );
};

export default PageContainer;
